import React, { useState } from 'react';
import { ShieldCheck, Check, Loader2, Scale, Clock, Mic } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../lib/api.js';

const CLAUSES = [
  { icon: Scale, title: 'Hold Harmless & Non-Liability', body: 'CIVIX LLC is solely a software and telephony tool provider. You assume sole legal responsibility for TCPA, DNC, state calling-hour and wiretapping compliance, and hold CIVIX LLC completely harmless from any fines, damages or legal actions.' },
  { icon: Clock, title: 'TCPA & TSR Compliance', body: 'Permissible calling hours, prior express written consent standards and opt-out mechanics. CIVIX enforces 8:00 AM – 8:00 PM prospect-local time.' },
  { icon: Mic, title: 'Call Recording Disclosure', body: 'You must notify consumers in two-party (all-party) consent states that the call is recorded.' },
];

export const normalizeName = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Producer Agreement signing step shown before Phone Setup.
 * @param {{data: {rendered?: string, legalName?: string}, onSigned: () => void}} props
 * @returns {JSX.Element}
 */
export default function ProducerAgreementStep({ data, onSigned }) {
  const legalName = data?.legalName || '';
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const nameOk = normalizeName(typedName) === normalizeName(legalName) && typedName.trim().length > 0;
  const canSign = agreed && nameOk && !signing;

  async function sign() {
    if (!canSign) return;
    setSigning(true);
    try {
      await apiPost('/onboarding/producer-agreement/sign', { typedName: typedName.trim() });
      onSigned();
    } catch (e) {
      const d = e.response?.data || {};
      if (d.code === 'already_signed') onSigned();
      else toast.error(d.error || e.message || 'Could not record signature.');
    } finally { setSigning(false); }
  }

  return (
    <div className="panel p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-civix-cyan" />
        <h2 className="text-lg font-semibold text-white">CIVIX Independent Producer &amp; Telephony Compliance Agreement <span className="text-white/40 text-sm">(v1.0)</span></h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {CLAUSES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-lg border border-civix-border/60 bg-black/20 p-3 text-xs text-white/70 space-y-1">
            <div className="flex items-center gap-1.5 text-white font-medium"><Icon className="w-3.5 h-3.5 text-civix-cyan" />{title}</div>
            <p>{body}</p>
          </div>
        ))}
      </div>
      <div className="h-72 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-white/80 bg-black/30 border border-civix-border/50 rounded-md p-4 font-mono" tabIndex={0} aria-label="Agreement text">
        {data?.rendered || ''}
      </div>
      <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" />
        I have read, understood, and agree to the CIVIX Independent Producer Agreement and compliance mandates.
      </label>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="block text-xs text-white/60">
          Full legal name (digital signature)
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={legalName || 'Your legal name'}
            className="mt-1 w-full px-3 py-2 rounded-md bg-black/30 border border-civix-border/50 text-white text-sm focus:outline-hidden focus:border-civix-purple/60"
          />
        </label>
        <div className="text-xs text-white/60">Date<div className="mt-1 px-3 py-2 rounded-md bg-black/20 border border-civix-border/40 text-white text-sm">{today}</div></div>
      </div>
      {typedName.length > 0 && !nameOk && (
        <div className="text-[11px] text-rose-300">Must exactly match your registered legal name{legalName ? ` (${legalName})` : ''}.</div>
      )}
      <button
        type="button"
        disabled={!canSign}
        onClick={sign}
        className={`w-full py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 border transition ${
          canSign ? 'bg-civix-purple/25 text-white border-civix-purple/50 hover:bg-civix-purple/40' : 'bg-white/5 text-white/40 border-white/10 cursor-not-allowed'
        }`}
      >
        {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Sign &amp; Continue to Phone Setup
      </button>
    </div>
  );
}
